// -----------------------------------------------------------------------------
// ScheduleReminderPanel  (Wave A)
//
// PURPOSE — CREATES A *FUTURE* REMINDER. This panel dispatches AddReminder to
// schedule a reminder (datetime + method + assignees + note) for later. It does
// NOT log a past contact event. Contrast with FollowUpPanel, which LOGS A PAST
// follow-up (call log / note / composer hand-off) and creates no reminder.
//
// Action panel body for SCHEDULE_REMINDER.
// Fields: date+time (default = now +1 day), assignee dropdown, note textarea.
// Submit: dispatch AddReminder thunk, then call onComplete.
// Assignees: sourced from state.practiceUser via FilterAssigneeList (security boundary).
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Stack,
    alpha,
} from '@mui/material';
import { dispatch, useSelector } from 'src/redux/store';
import { AddReminder } from 'src/redux/slices/lead/taskThunks';
import { updateError } from 'src/redux/slices/user';
import FilterAssigneeList from 'src/utils/filterAssigneeList';
import Iconify from 'src/components/Iconify';
import { toDatetimeLocalString } from 'src/utils/taskDateUtils';
import { ACTION_META } from '../actionMeta';

// ─── "How" method chips ───────────────────────────────────────────────────────
// Mirrors ScheduleTaskReminder's three curated action types for visual parity,
// but the Reminder model's `method` choices are UPPERCASE (REMINDER_METHOD_CHOICES
// in apps/todos/choices.py) — NOT the lowercase TASK_METHOD used by the Tasks API.
const METHOD_OPTIONS = [
    { method: 'CALL',     label: 'Call',     icon: ACTION_META.LOG_PHONE_CALL.icon, color: ACTION_META.LOG_PHONE_CALL.color },
    { method: 'WHATSAPP', label: 'WhatsApp', icon: ACTION_META.WHATSAPP_SENT.icon,  color: ACTION_META.WHATSAPP_SENT.color },
    { method: 'EMAIL',    label: 'Email',    icon: ACTION_META.EMAIL_SENT.icon,     color: ACTION_META.EMAIL_SENT.color },
];

const methodChipSx = (active, activeColor = '#00AB55') => ({
    px: 1.5,
    py: 0.5,
    borderRadius: '6px',
    border: '1px solid',
    borderColor: active ? activeColor : 'rgba(0,0,0,0.12)',
    bgcolor: active ? alpha(activeColor, 0.08) : '#ffffff',
    color: active ? activeColor : '#6e6e73',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    transition: 'all 150ms ease',
    '&:hover': { borderColor: activeColor, color: activeColor, bgcolor: alpha(activeColor, 0.06) },
});

/** Returns a datetime-local string ("YYYY-MM-DDTHH:MM") for now + 1 day, in
 *  LOCAL time (toDatetimeLocalString) so the picker isn't 1h early in BST. */
function defaultDateTime() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setSeconds(0);
    d.setMilliseconds(0);
    return toDatetimeLocalString(d); // "YYYY-MM-DDTHH:MM" in local time
}

export default function ScheduleReminderPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
}) {
    const [dateTime, setDateTime] = useState(defaultDateTime);
    const [assigneeId, setAssigneeId] = useState('');
    const [note, setNote] = useState('');
    const [method, setMethod] = useState('CALL');
    const [submitting, setSubmitting] = useState(false);

    const rawUsers = useSelector((state) => state.practiceUser?.practiceUser?.results || []);
    const assignees = useMemo(() => FilterAssigneeList(rawUsers), [rawUsers]);

    const handleSubmit = () => {
        if (!dateTime) {
            dispatch(updateError({ success: false, message: 'Please select a date and time' }));
            return;
        }
        const treatmentId = item?.rawTask?.lead_treatment?.id;
        if (!practiceId || !leadId || String(leadDetail?.id) !== String(leadId) || !treatmentId ||
            !leadDetail?.lead_treatments?.some((treatment) => String(treatment.id) === String(treatmentId))) {
            dispatch(updateError({ success: false, message: 'Cannot schedule reminder: treatment not identified. Please close and reopen.' }));
            return;
        }
        setSubmitting(true);

        const data = {
            lead: leadId,
            // BE field is `datetime` (was wrongly `date`, which the serializer
            // silently drops → reminder defaulted to now()). Value stays a real
            // UTC ISO string; the serializer parses ISO fine.
            datetime: new Date(dateTime).toISOString(),
            group: 'FOLLOW_UP',
            method,
            // `assignees` is an M2M array (was singular `assignee`, dropped by BE).
            assignees: assigneeId ? [assigneeId] : [],
            lead_treatment: treatmentId,
            // BE field is `note` (was wrongly `description`).
            ...(note ? { note } : {}),
        };

        dispatch(
            AddReminder(data, practiceId, ({ success, message }) => {
                setSubmitting(false);
                if (success) {
                    onComplete?.(item);
                } else {
                    dispatch(updateError({ success: false, message: message || 'Failed to schedule reminder' }));
                }
            })
        );
    };

    return (
        <Box
            data-testid="schedule-reminder-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(255, 171, 0, 0.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:bell-outline" width={18} height={18} sx={{ color: '#FFAB00' }} />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                    Schedule reminder
                </Typography>
            </Box>

            {/* Date + time */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Remind at
                </Typography>
                <TextField
                    data-testid="schedule-reminder-datetime"
                    type="datetime-local"
                    size="small"
                    fullWidth
                    value={dateTime}
                    onChange={(e) => setDateTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>

            {/* How (method) — Call / WhatsApp / Email, mirrors ScheduleTaskReminder */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    How
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
                    {METHOD_OPTIONS.map((opt) => {
                        const active = method === opt.method;
                        return (
                            <Box
                                key={opt.method}
                                data-testid={`schedule-reminder-method-${opt.method.toLowerCase()}`}
                                onClick={() => setMethod(opt.method)}
                                sx={methodChipSx(active, opt.color)}
                            >
                                <Iconify icon={opt.icon} width={13} height={13} sx={{ color: active ? opt.color : '#6e6e73' }} />
                                {opt.label}
                            </Box>
                        );
                    })}
                </Stack>
            </Box>

            {/* Assignee */}
            {assignees.length > 0 && (
                <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                        Assign to
                    </Typography>
                    <TextField
                        data-testid="schedule-reminder-assignee"
                        select
                        size="small"
                        fullWidth
                        value={assigneeId}
                        onChange={(e) => setAssigneeId(e.target.value)}
                        SelectProps={{ displayEmpty: true }}
                    >
                        <MenuItem value="">— Unassigned —</MenuItem>
                        {assignees.map((a) => (
                            <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                        ))}
                    </TextField>
                </Box>
            )}

            {/* Note */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Note (optional)
                </Typography>
                <TextField
                    data-testid="schedule-reminder-note"
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    placeholder="Add context for this reminder…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
            </Box>

            {/* Actions */}
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                <Button
                    data-testid="schedule-reminder-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={submitting || !dateTime}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:bell-check-outline" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#FFAB00',
                        color: '#fff',
                        '&:hover': { bgcolor: '#e09900' },
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Scheduling…' : 'Schedule'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

ScheduleReminderPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

ScheduleReminderPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
};
